import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import ReCAPTCHA from 'react-google-recaptcha'
import { useTranslation } from 'react-i18next'
import { Button, Grid, HelperText, Icon } from 'gu-ui-kit'
import { Field, Form, reduxForm } from 'redux-form'
import renderInputField from 'components/FormControls/textInput'
import validator from 'utils/asyncValidator'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import useRecaptchaOutsideClick from 'utils/hooks/useRecaptchaOutsideClick'
import formStyles from 'modules/Forms/common-form.module.scss'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { LOGIN_SUBMIT } from 'modules/Auth/types'
import { validationSchema } from 'modules/Forms/Auth/Login/validationSchema'
import { FormError } from 'modules/Forms/Auth/subcomponents'
import { IS_APP_PRODUCT_GETUNIQ } from 'utils/constants/product'

let LoginForm = ({ handleSubmit, submitting, form, error }) => {
  const { t } = useTranslation()
  const captchaRef = useRef(null)
  const [captchaKey, setCaptchaKey] = useState(null)
  const pathGen = useGeneratePathI18n()
  const { i18n } = useSelector(({ app }) => app)
  const breakpoint = useBreakpoint()

  useEffect(() => {
    setCaptchaKey(
      window.GOOGLE_CAPTCHA_SITE_KEY && !window.GOOGLE_CAPTCHA_SITE_KEY.match(/({{)|(}})/)
        ? window.GOOGLE_CAPTCHA_SITE_KEY
        : '6LdD4ncUAAAAANcnFRZ_epMrlwp7NktSu4rhcSpq',
    )
  }, [])

  useRecaptchaOutsideClick(form)

  const submit = async (values, dispatch) => {
    if (!captchaRef?.current) {
      return
    }

    captchaRef.current.reset()
    captchaRef.current.execute()

    const token = await captchaRef.current.executeAsync()

    if (!token) {
      return
    }

    new Promise((resolve, reject) =>
      dispatch({
        meta: { resolve, reject },
        type: LOGIN_SUBMIT,
        payload: {
          data: {
            ...values,
            'g-recaptcha-response': token,
          },
          formName: form,
        },
      }),
    )
  }

  return (
    <Form
      method={'POST'}
      onSubmit={handleSubmit(submit)}
      noValidate
    >
      {captchaKey && (
        <ReCAPTCHA
          ref={captchaRef}
          size="invisible"
          sitekey={captchaKey}
          hl={i18n}
        />
      )}
      <div className={'margin-bottom-16'}>
        <Field
          fullWidth
          name="email"
          type="email"
          component={renderInputField}
          t={t}
          label={t('auth::forms.login.fields.email.label')}
        />
      </div>
      <div>
        <Field
          fullWidth
          name="password"
          type="password"
          inputMode={'text'}
          component={renderInputField}
          autocomplete="new-password"
          t={t}
          label={t('auth::forms.login.fields.password.label')}
          clearButton
          revealButton
        />
        <HelperText
          className={formStyles['formControl_recover-link']}
          simpleHelperStyle
        >
          <Link to={pathGen(ROUTES.AUTH_RECOVER)}>
            {t('auth::forms.login.fields.password.helperText')}
          </Link>
        </HelperText>
      </div>

      <FormError error={error} />

      <Grid
        className={!breakpoint.fb ? 'margin-top-32' : 'margin-top-40'}
        direction={!breakpoint.fb ? 'column-reverse' : 'row'}
        alignItems={'center'}
        justifyContent={'start'}
        wrap={false}
        gap={16}
      >
        {IS_APP_PRODUCT_GETUNIQ && (
          <Button
            fullWidth
            component={'a'}
            target={'_blank'}
            rel={'noopener noreferrer'}
            href={ROUTES.AUTH_ADMITAD_AUTH}
            startIcon={
              <Icon
                size={'m'}
                name={'admitad'}
              />
            }
            variant={'secondary'}
            grey
            size={'l'}
          >
            Admitad
          </Button>
        )}

        <Button
          fullWidth
          disabled={submitting}
          size={'l'}
          variant={'primary'}
          type={'submit'}
        >
          {t('common::buttons.login')}
        </Button>
      </Grid>
    </Form>
  )
}

LoginForm = reduxForm({
  form: 'LoginForm',
  updateUnregisteredFields: true,
  enableReinitialize: true,
  initialValues: {
    email: '',
    password: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
  asyncValidate: validator(validationSchema),
})(LoginForm)

LoginForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  form: PropTypes.string,
  error: PropTypes.string,
}

LoginForm.defaultValues = {
  handleSubmit: () => {},
  submitting: false,
  form: 'LoginForm',
}

export default LoginForm
